import { FaceLandmark } from '@zappar/zappar-react-three-fiber';
import React, { ReactElement, FunctionComponent, memo, useMemo } from 'react';
import { Jute } from '../..';
import { useDataStore } from '../../zustand/data-store';
import withButtonBehaviour from '../hoc/withButtonBehaviour';
import withImageBehaviour from '../hoc/withImageBehaviour';
import withModel3dBehaviour from '../hoc/withModel3dBehaviour';
import withTextBehaviour from '../hoc/withTextBehaviour';
import withVideoBehaviour from '../hoc/withVideoBehaviour';
import { Button, Image, Video, Text, Text3d, Model3d, Emitter } from '@r3f/index';
import { IComponentType, IComponentUnion, IFaceLandmark, IFaceLandmarkGroup, ISceneComp, ITrackingTypes } from '@r3f-component-data-structure';
import { getFaceLandmarkPositionsByHeadbustType, uarTarget } from '@r3f/utils/face-tracking';
import withEmitterBehaviour from '../hoc/withEmitterBehaviour';
import withText3dBehaviour from '../hoc/withText3dBehaviour';

// Setting up hoc components
export const ImageHOC = memo(withImageBehaviour(Image));
ImageHOC.displayName = 'Image_HOC'; // helps with identification in debugger
export const ButtonHOC = memo(withButtonBehaviour(Button));
ButtonHOC.displayName = 'Button_HOC';
export const VideoHOC = memo(withVideoBehaviour(Video));
VideoHOC.displayName = 'Video_HOC';
export const TextHOC = memo(withTextBehaviour(Text));
TextHOC.displayName = 'Text_HOC';
export const Text3dHOC = memo(withText3dBehaviour(Text3d));
Text3dHOC.displayName = 'Text3d_HOC';
export const Model3dHOC = memo(withModel3dBehaviour(Model3d));
Model3dHOC.displayName = 'Model3d_HOC';
export const EmitterHOC = memo(withEmitterBehaviour(Emitter))

type IParentProps = {
	sceneId: string;
};

export const generateHocComponents = (componentIds: string[], sceneId: string, componentsById: { [id: string]: IComponentUnion }, isOrigin?: boolean): (ReactElement | null)[] => {
	if (!componentsById) return [];
	const components = componentIds.map((id) => {
		const componentType = componentsById[id].type;
		switch (componentType) {
			case IComponentType.Button:
				return <ButtonHOC key={id} sceneId={sceneId} id={id} />;
			case IComponentType.Image:
				return <ImageHOC key={id} sceneId={sceneId} id={id} />;
			case IComponentType.Text:
				return <TextHOC key={id} sceneId={sceneId} id={id} />;
			case IComponentType.Text3d:
				return <Text3dHOC key={id} sceneId={sceneId} id={id} />;
			case IComponentType.Video:
				return <VideoHOC key={id} sceneId={sceneId} id={id} />;
			case IComponentType.Model3d:
				return <Model3dHOC key={id} sceneId={sceneId} id={id} />;
			case IComponentType.Emitter:
				return <EmitterHOC key={id} sceneId={sceneId} id={id} />;
			default:
				return null;
		}
	});
	if (isOrigin) {
		return [<group key={`${sceneId}_origin`} name={`${sceneId}_origin`}>{<>{components}</>}</group>];
	} else {
		return components;
	}
};

interface IFaceLandmarkProps {
	target: string;
	landmark: IFaceLandmark;
	children?: React.ReactNode;
}

const FaceLandMark: FunctionComponent<IFaceLandmarkProps> = ({target, landmark, children}) => {
	return (<React.Fragment key={landmark}>
		{/* @ts-ignore-next-line */}
		<FaceLandmark key={landmark} name={landmark} target={target}>
			<group scale={2}>{children}</group>
		</FaceLandmark>
	</React.Fragment>)
}

const ArComponents: FunctionComponent<IParentProps> = ({ sceneId }) => {
	const componentsById = useDataStore((state) => state.componentsById);

	const arComponents: React.ReactNode = useMemo(() => {
		if (!componentsById) return null;
		const scene = componentsById?.[sceneId] as ISceneComp;
		if (scene.trackingType == ITrackingTypes.face) {
			let hasLipBottomFaceLandmark = false;
			const faceLandmarkGroupIds = scene.children.filter((componentId) => componentsById[componentId].type == IComponentType.FaceLandmarkGroup);

			const landmarkComponents = faceLandmarkGroupIds.map((id) => {
				const faceLandmarkGroup = componentsById[id] as IFaceLandmarkGroup;
				if (faceLandmarkGroup.landmark === IFaceLandmark.lipBottom) hasLipBottomFaceLandmark = true;
				const components = generateHocComponents(faceLandmarkGroup.children, sceneId, componentsById);
				const target = uarTarget(faceLandmarkGroup.landmark, true); // TODO: change to dynamic boolean once rear camera is also used.
				
				if (Jute.usesOrbitCamera) {
					const positionDict = getFaceLandmarkPositionsByHeadbustType('uar')
					const position = positionDict[faceLandmarkGroup.landmark];
					return (
						<group name={faceLandmarkGroup.landmark} key={faceLandmarkGroup.landmark} position={position} scale={1}>
							{components}
						</group>
					) 
				}
	
				if (target == 'origin') return null; // Origin needs to be child of <FaceTracked> and is loaded in <FaceTrackerWrapper> separately
				
				return <FaceLandMark key={faceLandmarkGroup.landmark} landmark={faceLandmarkGroup.landmark} target={target}>{components}</FaceLandMark>
			});

			if (!!scene.actions?.onMouthOpen && !Jute.usesOrbitCamera) {
				// we never use the lip top landmark so we always need to add if scene has mouth open actions to enable those to work
				landmarkComponents.push(<FaceLandMark key={IFaceLandmark.lipTop} landmark={IFaceLandmark.lipTop} target={uarTarget(IFaceLandmark.lipTop, true)} />)
				// if we don't use lip bottom landmark add it so that mouth open actions work
				if (!hasLipBottomFaceLandmark) {
					landmarkComponents.push(<FaceLandMark key={IFaceLandmark.lipBottom} landmark={IFaceLandmark.lipBottom} target={uarTarget(IFaceLandmark.lipBottom, true)} />)
				}
			}

			return landmarkComponents;
		} else {
			return generateHocComponents(scene.children, sceneId, componentsById);
		}
	}, [sceneId, componentsById]);

	return <>{arComponents}</>;
};

ArComponents.displayName = 'ArComponents';
export default memo(ArComponents);
